// pages/Customers/Customers.js - UI matched with Users Management page + City filter + Dynamic City Counts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Customers.css';
import {
  FaChevronLeft,
  FaChevronRight,
  FaStore,
  FaEdit,
  FaSearch,
  FaCheckDouble,
  FaUserPlus,
  FaBuilding,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { RiDeleteBinFill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaUserAlt } from "react-icons/fa";
import Swal from 'sweetalert2';

// Last loaded customers of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const CUSTOMERS_CACHE_PREFIX = 'customers_cache_';
const CUSTOMERS_CITIES_PREFIX = 'customers_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getShopIdFromPath = (pathname) => {
  const match = pathname.match(/\/shop\/(\d+)/);
  return match ? match[1] : null;
};

const Customers = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialShopId = getShopIdFromPath(location.pathname);
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cachedCustomers = initialShopId ? readJson(`${CUSTOMERS_CACHE_PREFIX}${initialShopId}`) : null;
    const cachedCities = initialShopId ? readJson(`${CUSTOMERS_CITIES_PREFIX}${initialShopId}`) : null;
    initialRef.current = {
      customers: Array.isArray(cachedCustomers) ? cachedCustomers : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage] = useState(10);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const [customers, setCustomers] = useState(initialRef.current.customers || []);
  const [masterCustomers, setMasterCustomers] = useState(initialRef.current.customers || []); // ✅ Keeps all customers for accurate counting
  // True only while there is nothing to show yet (so "No customers found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.customers);
  const [error, setError] = useState(null);

  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(initialShopId);
  const [selectedShopName, setSelectedShopName] = useState('');

  // City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);

  const customersRequestRef = useRef(0);
  const prevShopRef = useRef(initialShopId);
  const selectedShopNameRef = useRef('');

  // =========================
  // AUTH TOKEN
  // =========================
  const getAuthToken = () => {
    const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.access || parsed.token || parsed.access_token) {
            return parsed.access || parsed.token || parsed.access_token;
          }
          if (parsed.token && typeof parsed.token === 'object') {
            return parsed.token.access || parsed.token.token || parsed.token.access_token;
          }
        } catch (e) {
          return storedData;
        }
      }
    }
    return null;
  };

  // =========================
  // INITIALIZE (shops list is loaded once)
  // =========================
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch('https://super-backend.hira-tech.co.uk/user/shops/', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Failed to fetch shops');

        const data = await response.json();
        setShops(data);

        const urlShopId = getShopIdFromPath(location.pathname);
        if (urlShopId) {
          setSelectedShopId(urlShopId);
        } else if (data.length > 0) {
          const firstShopId = data[0].shop_id || data[0].id || data[0]._id;
          if (firstShopId) {
            setSelectedShopId(firstShopId.toString());
            navigate(`/shop/${firstShopId}/customers`, { replace: true });
          }
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to initialize.');
        setIsLoading(false);
      }
    };
    initializeComponent();
  }, []);

  // =========================
  // WATCH URL SHOP
  // =========================
  useEffect(() => {
    const urlShopId = getShopIdFromPath(location.pathname);
    if (urlShopId && urlShopId !== selectedShopId) {
      setSelectedShopId(urlShopId);
    }
  }, [location.pathname]);

  // =========================
  // SHOP NAME
  // =========================
  useEffect(() => {
    if (selectedShopId && shops.length > 0) {
      const shop = shops.find((s) => {
        const id = s.shop_id || s.id || s._id;
        return id && id.toString() === selectedShopId.toString();
      });
      const name = shop ? shop.shop_name || shop.name : 'Unknown Shop';
      setSelectedShopName(name);
      selectedShopNameRef.current = name;
    }
  }, [selectedShopId, shops]);

  // =========================
  // CITIES (header dropdown)
  // =========================
  const fetchFilterCities = async (shopId) => {
    if (!shopId) return;
    // The dropdown only shows "Loading..." when there is no saved list yet
    const hasSaved = Array.isArray(readJson(`${CUSTOMERS_CITIES_PREFIX}${shopId}`));
    try {
      if (!hasSaved) setLoadingFilterCities(true);
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/shop-cities/?shop_id=${shopId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      let citiesData = [];
      if (response.ok) {
        const data = await response.json();
        if (data) {
          if (Array.isArray(data.cities)) {
            citiesData = data.cities;
          } else if (Array.isArray(data)) {
            citiesData = data;
          } else if (typeof data.cities === 'string') {
            try {
              citiesData = JSON.parse(data.cities);
            } catch {
              citiesData = data.cities.split(',').map(c => c.trim()).filter(c => c);
            }
          }
        }
      }

      setFilterCities(citiesData);
      writeJson(`${CUSTOMERS_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (err) {
      console.error('Error fetching shop cities:', err);
      setFilterCities([]);
    } finally {
      setLoadingFilterCities(false);
    }
  };

  // =========================
  // FETCH CUSTOMERS
  // Runs in the background: only the latest request is allowed to update the screen.
  // =========================
  const fetchCustomers = useCallback(async (cityFilter = null) => {
    const requestId = ++customersRequestRef.current;
    try {
      setError(null);

      const token = getAuthToken();
      if (!token) throw new Error('No authentication token found.');

      const params = new URLSearchParams();
      if (selectedShopId) params.append('shop_id', selectedShopId);
      if (cityFilter && cityFilter !== '' && cityFilter !== 'all') {
        params.append('city', cityFilter);
      }

      const qs = params.toString();
      const apiUrl = `https://super-backend.hira-tech.co.uk/user/getAllcustomers/${qs ? `?${qs}` : ''}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (requestId !== customersRequestRef.current) return;
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (requestId !== customersRequestRef.current) return;

      const formattedCustomers = data.map((customer) => ({
        id: customer.id,
        firstName: customer.first_Name || '',
        lastName: customer.last_Name || '',
        email: customer.email || '',
        phone: customer.phone_Number || '',
        companyName:
          customer.company_name ||
          customer.company_Name ||
          customer.Company_name ||
          customer.CompanyName ||
          customer.company ||
          '',
        shopName: customer.shop_name || selectedShopNameRef.current,
        shopId: customer.shop || selectedShopId,
        selectedCity: customer.selected_city || '',
        applyVat: !!customer.apply_vat,
        applyDeliveryCharges: !!customer.apply_delivery_charges
      }));

      setCustomers(formattedCustomers);
      setSelectedCustomers([]);
      setCurrentPage(1);

      // ✅ Update master list ONLY when fetching all cities to keep accurate counts
      if (!cityFilter || cityFilter === '' || cityFilter === 'all') {
        setMasterCustomers(formattedCustomers);
        if (selectedShopId) writeJson(`${CUSTOMERS_CACHE_PREFIX}${selectedShopId}`, formattedCustomers);
      }

    } catch (err) {
      if (requestId === customersRequestRef.current) setError(err.message);
    } finally {
      if (requestId === customersRequestRef.current) setIsLoading(false);
    }
  }, [selectedShopId]);

  // Shop changed: show the saved list of this shop immediately (if there is one),
  // then load customers and cities together.
  useEffect(() => {
    if (!selectedShopId) {
      setFilterCities([]);
      setSelectedFilterCity('');
      return;
    }

    if (prevShopRef.current !== selectedShopId) {
      prevShopRef.current = selectedShopId;
      const cachedCustomers = readJson(`${CUSTOMERS_CACHE_PREFIX}${selectedShopId}`);
      const cachedCities = readJson(`${CUSTOMERS_CITIES_PREFIX}${selectedShopId}`);
      if (Array.isArray(cachedCustomers)) {
        setCustomers(cachedCustomers);
        setMasterCustomers(cachedCustomers);
        setIsLoading(false);
      } else {
        setCustomers([]);
        setMasterCustomers([]);
        setIsLoading(true);
      }
      setFilterCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedFilterCity('');
      setSelectedCustomers([]);
      setCurrentPage(1);
    }

    fetchCustomers();
    fetchFilterCities(selectedShopId);
  }, [selectedShopId, fetchCustomers]);

  // City change: the list is filtered instantly from the loaded customers,
  // and refreshed from the server in the background.
  const applyCity = (newCity) => {
    setSelectedFilterCity(newCity);
    setCurrentPage(1);
    setSelectedCustomers([]);
    setCustomers(newCity ? masterCustomers.filter((c) => c.selectedCity === newCity) : masterCustomers);
    fetchCustomers(newCity || null);
  };

  const handleFilterCityChange = (e) => {
    applyCity(e.target.value);
  };

  // =========================
  // SEARCH
  // =========================
  const filteredCustomers = customers.filter((customer) => {
    const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
    const email = (customer.email || '').toLowerCase();
    const phone = customer.phone || '';
    const company = (customer.companyName || '').toLowerCase();
    const city = (customer.selectedCity || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      phone.includes(searchTerm) ||
      company.includes(searchLower) ||
      city.includes(searchLower)
    );
  });

  // =========================
  // PAGINATION
  // =========================
  const totalCustomers = filteredCustomers.length;
  const totalPages = Math.ceil(totalCustomers / rowsPerPage);
  const indexOfLastCustomer = currentPage * rowsPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - rowsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

  // =========================
  // SELECT ALL
  // =========================
  const handleSelectAll = () => {
    if (selectedCustomers.length === currentCustomers.length && currentCustomers.length > 0) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(currentCustomers.map((customer) => customer.id));
    }
  };

  const handleSelectCustomer = (id) => {
    if (selectedCustomers.includes(id)) {
      setSelectedCustomers(selectedCustomers.filter((customerId) => customerId !== id));
    } else {
      setSelectedCustomers([...selectedCustomers, id]);
    }
  };

  // =========================
  // ADD / EDIT / DELETE
  // =========================
  const handleAddCustomer = () => {
    if (!selectedShopId) return;
    navigate(`/shop/${selectedShopId}/add-user?type=CUSTOMER&returnTo=customers`);
  };

  const handleEdit = (customer) => {
    if (!selectedShopId) return;
    navigate(`/shop/${selectedShopId}/edit-customer/${customer.id}?returnTo=customers`);
  };

  const handleDelete = async (id) => {
    const customer = customers.find(c => c.id === id);
    const customerName = customer
      ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'this customer'
      : 'this customer';

    const result = await Swal.fire({
      title: 'Delete Customer?',
      html: `Are you sure you want to delete <strong>${customerName}</strong>?<br/><small style="color:#dc3545;">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token found.');

      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/delete/${id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to delete customer. Status: ${response.status}`);

      // Keep the counts and the saved list correct right away
      const newMaster = masterCustomers.filter((c) => c.id !== id);
      setMasterCustomers(newMaster);
      if (selectedShopId) writeJson(`${CUSTOMERS_CACHE_PREFIX}${selectedShopId}`, newMaster);
      setCustomers((prev) => prev.filter((c) => c.id !== id));

      await fetchCustomers(selectedFilterCity || null);

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${customerName} has been deleted successfully.`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: `Failed to delete: ${err.message}`,
        confirmButtonColor: '#004aad'
      });
    }
  };

  // ✅ Calculate total customers for "All Cities" (using master list)
  const allCitiesCount = masterCustomers.length;

  // ✅ Helper to calculate count for a specific city (using master list)
  const getCityCount = (cityName) => {
    return masterCustomers.filter(customer => customer.selectedCity === cityName).length;
  };

  if (error) {
    return (
      <div className="customers-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchCustomers(selectedFilterCity || null)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customers-page">

      {/* HEADER - Users Management style */}
      <div className="customers-header-section">
        <div className="customers-header-left">
          <FaUserAlt className="customers-header-icon" />
          <h2 className="customers-header-title">Customers Management</h2>

          {/* ✅ Dynamic City Badge with Count */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity || 'All Cities'} ({selectedFilterCity ? getCityCount(selectedFilterCity) : allCitiesCount})
            </span>
          </span>
        </div>

        <div className="customers-header-right">
          {/* City Dropdown with IoMdArrowDropdown and Dynamic Counts */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || (filterCities.length === 0 && !isLoading)}
            >
              {/* ✅ Dynamic "All Cities" option showing total count */}
              <option value="">
                All Cities ({allCitiesCount})
              </option>

              {/* ✅ Dynamic city options showing their respective counts */}
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({getCityCount(city)})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          {/* Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search customers..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>Select All</span>
          </button>

          <button
            className="add-customer-btn"
            onClick={handleAddCustomer}
            disabled={!selectedShopId}
            title={!selectedShopId ? 'Select a shop first' : 'Add a new customer'}
          >
            <FaUserPlus />
            <span>ADD CUSTOMER</span>
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="customers-table-container">
        <div className="customers-table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedCustomers.length === currentCustomers.length &&
                      currentCustomers.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>City</th>
                <th>Billing</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {/* Nothing is shown until the first load is done, so "No customers found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="8" style={{ height: '200px' }}></td>
                </tr>
              ) : currentCustomers.length > 0 ? (
                currentCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={selectedCustomers.includes(customer.id) ? 'selected' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => handleSelectCustomer(customer.id)}
                      />
                    </td>
                    <td>
                      <div className="customer-name-cell">
                        {customer.firstName} {customer.lastName}
                      </div>
                    </td>
                    <td>{customer.email}</td>
                    <td>{customer.phone}</td>

                    <td className="company-cell">
                      {customer.companyName ? (
                        <span className="company-text">{customer.companyName}</span>
                      ) : (
                        <span className="company-none">—</span>
                      )}
                    </td>

                    <td>
                      <span className="user-city-cell">
                        {customer.selectedCity || '—'}
                      </span>
                    </td>

                    <td className="billing-cell">
                      <div className="billing-cell-content">
                        {customer.applyVat && (
                          <span className="billing-badge vat-badge">VAT</span>
                        )}
                        {customer.applyDeliveryCharges && (
                          <span className="billing-badge delivery-badge">Delivery</span>
                        )}
                        {!customer.applyVat && !customer.applyDeliveryCharges && (
                          <span className="billing-none">—</span>
                        )}
                      </div>
                    </td>

                    <td className="actions-cell">
                      <div className="actions-cell-inner">
                        <button
                          className="edit-customer-btn"
                          onClick={() => handleEdit(customer)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="delete-customer-btn"
                          onClick={() => handleDelete(customer.id)}
                          title="Delete"
                        >
                          <RiDeleteBinFill />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data-cell">
                    <FaBuilding className="no-data-icon" />
                    <p>
                      {selectedFilterCity
                        ? `No customers found in ${selectedFilterCity}.`
                        : 'No customers found.'}
                    </p>
                    {selectedFilterCity && (
                      <button
                        className="clear-city-filter-btn"
                        onClick={() => applyCity('')}
                      >
                        Clear City Filter
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      <div className="pagination-section">
        <div className="pagination-wrapper">
          <div className="pagination-info">
            {totalCustomers > 0
              ? `${indexOfFirstCustomer + 1}-${Math.min(indexOfLastCustomer, totalCustomers)} of ${totalCustomers}`
              : '0-0 of 0'}
          </div>

          <button
            className="page-btn"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
          >
            <FaChevronLeft /> Previous
          </button>

          <span className="current-page">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            className="page-btn"
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next <FaChevronRight />
          </button>
        </div>
      </div>

    </div>
  );
};

export default Customers;